import React from 'react'
import {
  BasicDetailsProfile,
  calculateAge,
  formatDateOfBirthDisplay,
} from '../../lib/utils/onboarding'
import '../onboarding/basic-details/basic-details.scss'
import './profile-general-readonly.scss'

interface ProfileGeneralReadonlyProps {
  profile: BasicDetailsProfile
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className='basic-details-page__field-label'>{children}</span>
)

const SectionHeader = ({ step, title }: { step: string; title: string }) => (
  <div className='basic-details-page__section-header basic-details-page__section-header--compact'>
    <span className='basic-details-page__section-icon' aria-hidden='true'>
      {step}
    </span>
    <div>
      <h3 className='basic-details-page__section-title'>{title}</h3>
    </div>
  </div>
)

const ReadonlyValue = ({
  label,
  value,
  testId,
}: {
  label: string
  value: string
  testId?: string
}) => (
  <div className='basic-details-page__field'>
    <FieldLabel>{label}</FieldLabel>
    <div className='profile-readonly-value' data-testid={testId}>
      {value || '—'}
    </div>
  </div>
)

const ProfileGeneralReadonly = ({ profile }: ProfileGeneralReadonlyProps) => {
  const age = calculateAge(profile.dateOfBirth)
  const ageDisplay = age !== null ? `${age} years` : '—'

  return (
    <div className='profile-general-readonly' data-testid='profile-general-readonly'>
      <div
        className='general-form-readonly-notice'
        role='note'
        data-testid='profile-general-readonly-notice'
      >
        Your details were saved during onboarding and cannot be changed here. Contact support if you
        need to update them.
      </div>

      <section className='basic-details-page__section' aria-labelledby='profile-basic-info'>
        <SectionHeader step='A' title='Basic details' />
        <div className='basic-details-page__field-row'>
          <ReadonlyValue
            label='First name'
            value={profile.firstName}
            testId='profile-readonly-first-name'
          />
          <ReadonlyValue
            label='Last name'
            value={profile.lastName}
            testId='profile-readonly-last-name'
          />
          <div className='basic-details-page__dob-age-group basic-details-page__field--full'>
            <div className='basic-details-page__field-row basic-details-page__field-row--identity'>
              <ReadonlyValue
                label='Date of birth'
                value={formatDateOfBirthDisplay(profile.dateOfBirth)}
                testId='profile-readonly-dob'
              />
              <div className='basic-details-page__field basic-details-page__field--age-col'>
                <FieldLabel>Age</FieldLabel>
                <div className='basic-details-page__age-value' data-testid='profile-readonly-age'>
                  {ageDisplay}
                </div>
              </div>
              <ReadonlyValue
                label='Phone number'
                value={profile.callingPhone}
                testId='profile-readonly-calling-phone'
              />
            </div>
          </div>
          <div className='basic-details-page__field-row basic-details-page__field-row--countries basic-details-page__field--full'>
            <ReadonlyValue
              label='Country of residence'
              value={profile.countryOfResidence}
              testId='profile-readonly-country'
            />
            <ReadonlyValue
              label='Tax residency'
              value={profile.taxResidency}
              testId='profile-readonly-tax-residency'
            />
            <ReadonlyValue
              label='Citizenship'
              value={profile.citizenship}
              testId='profile-readonly-citizenship'
            />
          </div>
        </div>
      </section>

      <section className='basic-details-page__section' aria-labelledby='profile-contact-details'>
        <SectionHeader step='B' title='Account' />
        <ReadonlyValue label='Email' value={profile.email} testId='profile-readonly-email' />
      </section>
    </div>
  )
}

export { ProfileGeneralReadonly }
